export interface DescriptionState {
  lastTime: number;
  finished: boolean;
}

export const initialDescriptionState: DescriptionState = { lastTime: 0, finished: false };

/**
 * Our videos loop forever. Announcing the descriptions on every pass would repeat the same
 * sentences at a screen reader user indefinitely, with no way to switch it off — worse than
 * announcing nothing. So only the first pass is announced: a currentTime that jumps backwards
 * means the loop wrapped, and from then on the live region stays empty.
 */
export const advanceDescriptionState = (
  state: DescriptionState,
  currentTime: number
): DescriptionState => ({
  lastTime: currentTime,
  finished: state.finished || currentTime < state.lastTime,
});

/** Joins whatever cues are active right now; VTT allows overlapping cues. */
export const getActiveCueText = (cues: ArrayLike<{ text?: string }> | null | undefined): string =>
  cues
    ? Array.from(cues)
        .map((cue) => cue.text ?? '')
        .filter(Boolean)
        .join(' ')
    : '';

interface DescriptionEventTarget {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface DescriptionVideo extends DescriptionEventTarget {
  currentTime: number;
}

export interface DescriptionTrack extends DescriptionEventTarget {
  mode: TextTrackMode;
  activeCues: ArrayLike<{ text?: string }> | null;
}

/**
 * Connects a video and its descriptions track to a live region, and returns the function that
 * disconnects them again.
 *
 * Returns `undefined` when either is missing, which is the normal state on the first render:
 * the video only mounts once the page is idle. The caller must therefore re-run this once the
 * video appears - passing that idle flag as an effect dependency - or nothing is ever announced.
 */
export const wireDescriptionAnnouncer = (
  video: DescriptionVideo | null | undefined,
  track: DescriptionTrack | null | undefined,
  announce: (text: string) => void
): (() => void) | undefined => {
  if (!video || !track) return undefined;

  track.mode = 'hidden'; // cues fire, nothing is painted over the video
  let state = initialDescriptionState;

  const onTimeUpdate = () => {
    state = advanceDescriptionState(state, video.currentTime);
    if (state.finished) announce('');
  };
  const onCueChange = () => {
    if (state.finished) return;
    announce(getActiveCueText(track.activeCues));
  };

  video.addEventListener('timeupdate', onTimeUpdate);
  track.addEventListener('cuechange', onCueChange);

  return () => {
    video.removeEventListener('timeupdate', onTimeUpdate);
    track.removeEventListener('cuechange', onCueChange);
  };
};
